import { ComponentRef, Injectable, ViewContainerRef } from "@angular/core";
import { IValidationRule } from "./IValidationRule";
import { ValidationMessageContainerComponent } from "./validation-message-container/validation-message-container.component";
import { ValidationMessagePopoverComponent } from "./validation-message-popover/validation-message-popover.component";
import { ValidationRuleDisplayType } from "./validation-rule.directive";

export type RuleContainer = { rule: IValidationRule; container: ValidationMessageContainerComponent; element: HTMLElement };
export type RulePopover = { rule: IValidationRule; componentRef: ComponentRef<ValidationMessagePopoverComponent>; element: HTMLElement };
@Injectable({
    providedIn: "root"
})
export class ValidationService {

    private readonly ruleContainers: RuleContainer[] = [];
    private readonly dyanmicMessageContainers: ComponentRef<ValidationMessageContainerComponent>[] = [];
    private readonly rulePopovers: RulePopover[] = [];

    removeMessageForRule(
        rule: IValidationRule,
        element: HTMLElement,
        raskRuleDisplayType: ValidationRuleDisplayType): void {

        if (raskRuleDisplayType === "Popover") {
            this.removePopoverMessage(rule, element);
        } else {
            this.removeContainerMessage(rule, element);
        }
    }

    showMessageForRule(
        viewContainerRef: ViewContainerRef,
        caller: HTMLElement,
        rule: IValidationRule,
        raskRuleDisplayType: ValidationRuleDisplayType,
        rowDepth?: number,
        targetContainerId?: string): void {

        if (raskRuleDisplayType === "Popover") {
            this.showPopoverMessage(viewContainerRef, caller, rule);
        } else {
            this.showContainerMessage(viewContainerRef, caller, rule, rowDepth, targetContainerId);
        }
    }

    private showContainerMessage(
        viewContainerRef: ViewContainerRef,
        caller: HTMLElement,
        rule: IValidationRule,
        rowDepth?: number,
        targetContainerId?: string): void {
        const componentInstance = this.createOrGetContainer(caller, viewContainerRef, rowDepth, targetContainerId);
        componentInstance.addNonExistingRule(rule, this.ruleContainers, caller);
    }

    private showPopoverMessage(
        viewContainerRef: ViewContainerRef,
        caller: HTMLElement,
        rule: IValidationRule): void {
        const componentRef = this.createValidationMessagePopoverComponent(viewContainerRef);
        componentRef.instance.rule = rule;
        caller.insertAdjacentElement("beforeend", componentRef.location.nativeElement);
        this.rulePopovers.push({
            rule: rule,
            componentRef: componentRef,
            element: caller
        });
    }

    private removeContainerMessage(
        rule: IValidationRule,
        element: HTMLElement): void {
        const ruleContainer = this.ruleContainers.find(rc => rc.rule?.message?.text === rule?.message?.text && rc.element === element);
        if (!ruleContainer) {
            return;
        }
        const instance = ruleContainer.container;
        instance.rules.splice(instance.rules.indexOf(rule), 1);
        this.ruleContainers.splice(this.ruleContainers.indexOf(ruleContainer), 1);

        if (instance.dynamic && instance.rules.length === 0) {
            const index = this.dyanmicMessageContainers.findIndex(m => m.instance === ruleContainer.container);
            const ref = this.dyanmicMessageContainers[index];
            this.dyanmicMessageContainers.splice(index, 1);
            ref.destroy();
        }
    }

    private removePopoverMessage(
        rule: IValidationRule,
        element: HTMLElement): void {
        const rulePopover = this.rulePopovers.find(rc => rc.rule?.message?.text === rule?.message?.text && rc.element === element);
        if (!rulePopover) {
            return;
        }

        this.rulePopovers.splice(this.rulePopovers.indexOf(rulePopover), 1);
        rulePopover.componentRef.destroy();
    }

    private getStaticContainer(id: string) {
        return ValidationMessageContainerComponent.StaticContainers.find(m => m.id === id);
    }

    private createOrGetContainer(
        caller: HTMLElement,
        viewContainerRef: ViewContainerRef,
        rowDepth: number,
        targetContainerId: string) {
        const targetElement = this.getCallersRowElement(caller, rowDepth);

        if (targetContainerId) {
            return this.getStaticContainer(targetContainerId);
        }

        const insertBeforeElement = this.findFirstElementOnNextRow(targetElement);
        let componentRef = this.findMessageContainerAtPosition(insertBeforeElement?.offsetTop);

        if (!componentRef) {
            componentRef = this.createValidationMessageContainerComponent(viewContainerRef);
            componentRef.instance.dynamic = true;
            this.dyanmicMessageContainers.push(componentRef);
            targetElement.parentElement.insertBefore(componentRef.location.nativeElement, insertBeforeElement);
        }

        return componentRef.instance;
    }

    private findMessageContainerAtPosition(top: number) {
        return top ? this.dyanmicMessageContainers.find(m => m.instance.elementPosY === top) : null;
    }

    private getCallersRowElement(caller: HTMLElement, rowDepth: number) {
        let targetElement = caller;
        if (rowDepth) {
            for (let i = 0; i < rowDepth; i++) {
                targetElement = targetElement.parentElement;
            }
        }
        return targetElement;
    }

    private findFirstElementOnNextRow(source: HTMLElement): HTMLElement {
        let currentSibling = source;
        while (currentSibling) {
            currentSibling = currentSibling.nextElementSibling as HTMLElement;
            if (!currentSibling || currentSibling.offsetTop > source.offsetTop) {
                return currentSibling;
            }
        }
        return undefined;
    }

    private createValidationMessageContainerComponent(ref: ViewContainerRef): ComponentRef<ValidationMessageContainerComponent> {
        return ref.createComponent(ValidationMessageContainerComponent);
    }

    private createValidationMessagePopoverComponent(ref: ViewContainerRef): ComponentRef<ValidationMessagePopoverComponent> {
        return ref.createComponent(ValidationMessagePopoverComponent);
    }
}
